import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Typography,
    alpha,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
    ArrowBack as ArrowBackIcon,
    EditOutlined as EditIcon,
    PeopleOutline as PeopleIcon,
    FolderOutlined as ProjectIcon,
    PlaceOutlined as SiteIcon,
    BoltOutlined as DeploymentIcon,
    InsertDriveFileOutlined as FileIcon,
    BusinessOutlined as BusinessIcon,
    LanguageOutlined as DomainIcon,
    PersonOutline as ContactIcon,
    CalendarTodayOutlined as CalendarIcon,
    CheckCircleOutline as StatusIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import clientService from 'src/clientService';
import { OrgMemberResponse, OrganisationResponse, ProjectAdminSummary } from 'src/api/Api';
import {
    getSystemTools,
    getOrganisationTools,
    provisionOrganisationTool,
    activateOrganisationTool,
    suspendOrganisationTool,
    OrganisationTool,
    SystemTool,
} from 'src/api2/toolsApi';
import { useSnackbar } from 'src/view/components/SnackbarService';
import {
    ORGANISATION_AVATAR_COLOR,
    formatDate,
    formatStatusLabel,
    getOrganisationInitials,
    getStatusColor,
} from './organisationUtils';
import { AssignProjectOrgModal } from '../components/AssignProjectOrgModal';

interface ApiErrorShape {
    detail?: string | { msg?: string }[] | Record<string, any>;
    error?: { message?: string };
    message?: string;
}

const getErrorMessage = (error: unknown, fallback: string): string => {
    const apiError = error as ApiErrorShape;
    if (typeof apiError?.detail === 'string') return apiError.detail;
    if (Array.isArray(apiError?.detail) && apiError.detail[0]?.msg) return apiError.detail[0].msg;
    if (apiError?.error?.message) return apiError.error.message;
    if (apiError?.message) return apiError.message;
    return fallback;
};

const StatCard: React.FC<{
    icon: React.ElementType;
    label: string;
    value: string | number;
    helper: string;
    color: string;
}> = ({ icon: Icon, label, value, helper, color }) => (
    <Box
        sx={{
            p: 2.5,
            bgcolor: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: '12px',
            minHeight: 118,
        }}
    >
        <Box
            sx={{
                width: 30,
                height: 30,
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(color, 0.12),
                color,
                mb: 2,
            }}
        >
            <Icon sx={{ fontSize: 17 }} />
        </Box>
        <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.72rem', mb: 0.5 }}>
            {label}
        </Typography>
        <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: '1.45rem', lineHeight: 1 }}>
            {value}
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.32)', fontSize: '0.62rem', mt: 0.8 }}>
            {helper}
        </Typography>
    </Box>
);

const InfoItem: React.FC<{
    icon: React.ElementType;
    label: string;
    value: string;
    color?: string;
}> = ({ icon: Icon, label, value, color = '#fff' }) => (
    <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1 }}>
            <Icon sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 15 }} />
            <Typography sx={{ color: 'rgba(255,255,255,0.42)', fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase' }}>
                {label}
            </Typography>
        </Box>
        <Typography noWrap sx={{ color, fontSize: '0.9rem', fontWeight: 800 }}>
            {value}
        </Typography>
    </Box>
);

const OrganisationOverviewPage: React.FC = () => {
    const { orgId } = useParams<{ orgId: string }>();
    const navigate = useNavigate();
    const { showSnackbar, snackbarType } = useSnackbar();
    const [organisation, setOrganisation] = useState<OrganisationResponse | null>(null);
    const [members, setMembers] = useState<OrgMemberResponse[]>([]);
    const [systemTools, setSystemTools] = useState<SystemTool[]>([]);
    const [orgTools, setOrgTools] = useState<OrganisationTool[]>([]);
    const [assignedProjects, setAssignedProjects] = useState<ProjectAdminSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const totalSitesCount = useMemo(() => {
        return assignedProjects.reduce((acc, p) => acc + (p.sites_count ?? (p as any).sites?.length ?? 0), 0);
    }, [assignedProjects]);

    const fetchOverview = useCallback(async () => {
        if (!orgId) {
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const [orgResponse, memberResponse, sysToolsRes, orgToolsRes, allProjectsRes] = await Promise.all([
                clientService.api.getOrganisationApiV1OrganisationsOrgIdGet(orgId),
                clientService.api.listOrganisationMembersApiV1OrganisationsOrgIdMembersGet(orgId, {
                    skip: 0,
                    limit: 500,
                }),
                getSystemTools().catch(() => []),
                getOrganisationTools(orgId).catch(() => []),
                clientService.api.getAllProjectsAdminApiV1AdminProjectsGet().catch(() => ({ data: [] as ProjectAdminSummary[] })),
            ]);
            setOrganisation(orgResponse.data);
            setMembers(memberResponse.data.data || []);
            setSystemTools(sysToolsRes);
            setOrgTools(orgToolsRes);

            const allProjectsList: ProjectAdminSummary[] = Array.isArray(allProjectsRes.data)
                ? allProjectsRes.data
                : (allProjectsRes.data as any)?.data || [];
            const assigned = allProjectsList.filter((p: ProjectAdminSummary) => p.org_id === orgId);
            setAssignedProjects(assigned);
        } catch (err) {
            const errorMessage = getErrorMessage(err, 'Failed to fetch organisation overview.');
            setError(errorMessage);
            showSnackbar({ message: `Error: ${errorMessage}`, type: snackbarType.ERROR });
        } finally {
            setLoading(false);
        }
    }, [orgId, showSnackbar, snackbarType.ERROR]);

    const handleUnassignProject = async (projectId: string) => {
        try {
            await clientService.api.updateProjectApiV1ProjectsProjectIdPut(projectId, { org_id: null } as any);
            showSnackbar({ message: 'Project unassigned successfully.', type: snackbarType.SUCCESS });
            fetchOverview();
        } catch (err) {
            const msg = getErrorMessage(err, 'Failed to unassign project.');
            showSnackbar({ message: `Error: ${msg}`, type: snackbarType.ERROR });
        }
    };

    const handleProvisionTool = async (toolId: string) => {
        if (!orgId) return;
        setActionLoadingId(toolId);
        try {
            await provisionOrganisationTool(orgId, toolId);
            showSnackbar({ message: 'Tool provisioned for organisation successfully.', type: snackbarType.SUCCESS });
            const updated = await getOrganisationTools(orgId);
            setOrgTools(updated);
        } catch (err) {
            const msg = getErrorMessage(err, 'Failed to provision tool.');
            showSnackbar({ message: `Error: ${msg}`, type: snackbarType.ERROR });
        } finally {
            setActionLoadingId(null);
        }
    };

    const handleActivateTool = async (orgToolId: string) => {
        if (!orgId) return;
        setActionLoadingId(orgToolId);
        try {
            await activateOrganisationTool(orgToolId);
            showSnackbar({ message: 'Tool activated successfully.', type: snackbarType.SUCCESS });
            const updated = await getOrganisationTools(orgId);
            setOrgTools(updated);
        } catch (err) {
            const msg = getErrorMessage(err, 'Failed to activate tool.');
            showSnackbar({ message: `Error: ${msg}`, type: snackbarType.ERROR });
        } finally {
            setActionLoadingId(null);
        }
    };

    const handleSuspendTool = async (orgToolId: string) => {
        if (!orgId) return;
        setActionLoadingId(orgToolId);
        try {
            await suspendOrganisationTool(orgToolId);
            showSnackbar({ message: 'Tool suspended for organisation.', type: snackbarType.SUCCESS });
            const updated = await getOrganisationTools(orgId);
            setOrgTools(updated);
        } catch (err) {
            const msg = getErrorMessage(err, 'Failed to suspend tool.');
            showSnackbar({ message: `Error: ${msg}`, type: snackbarType.ERROR });
        } finally {
            setActionLoadingId(null);
        }
    };

    useEffect(() => {
        fetchOverview();
    }, [fetchOverview]);

    if (loading && !organisation) {
        return (
            <Box sx={{ px: 4, py: 10, bgcolor: '#0D0D0D', minHeight: '100%', borderRadius: '16px', display: 'flex', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: '#10B981' }} />
            </Box>
        );
    }

    if (error || !organisation) {
        return (
            <Box sx={{ px: 4, py: 10, bgcolor: '#0D0D0D', minHeight: '100%', borderRadius: '16px', textAlign: 'center' }}>
                <Typography sx={{ color: '#F87171', mb: 2 }}>{error || 'Organisation not found.'}</Typography>
                <Button variant="outlined" onClick={fetchOverview}>Retry</Button>
            </Box>
        );
    }

    const statusColor = getStatusColor(organisation.status);
    const primaryContact = members.find(member => member.role?.toLowerCase().includes('admin'))?.user?.email
        || members[0]?.user?.email
        || '--';

    return (
        <Box sx={{ px: 4, py: 4, bgcolor: '#05090A', minHeight: '100%', borderRadius: '16px' }}>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/admin/organisations')}
                sx={{ color: '#10B981', textTransform: 'none', fontWeight: 700, mb: 3, px: 0, '&:hover': { bgcolor: 'transparent', color: '#34D399' } }}
            >
                Back to Organisations
            </Button>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, minWidth: 0 }}>
                    <Avatar
                        sx={{
                            width: 64,
                            height: 64,
                            borderRadius: '16px',
                            bgcolor: alpha(ORGANISATION_AVATAR_COLOR, 0.15),
                            color: ORGANISATION_AVATAR_COLOR,
                            fontWeight: 900,
                            fontSize: '1.4rem',
                            border: `1px solid ${alpha(ORGANISATION_AVATAR_COLOR, 0.35)}`,
                        }}
                    >
                        {getOrganisationInitials(organisation.name)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ color: '#fff', fontSize: { xs: '1.7rem', md: '2rem' }, fontWeight: 900, lineHeight: 1.1 }}>
                            {organisation.name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mt: 1, flexWrap: 'wrap' }}>
                            <Typography sx={{ color: 'rgba(255,255,255,0.48)', fontSize: '0.78rem' }}>
                                {organisation.domain || 'No domain configured'}
                            </Typography>
                            <Chip
                                label={formatStatusLabel(organisation.status)}
                                size="small"
                                sx={{
                                    height: 22,
                                    bgcolor: alpha(statusColor, 0.12),
                                    color: statusColor,
                                    border: `1px solid ${alpha(statusColor, 0.28)}`,
                                    fontSize: '0.66rem',
                                    fontWeight: 800,
                                }}
                            />
                            <Typography sx={{ color: 'rgba(255,255,255,0.42)', fontSize: '0.75rem' }}>
                                Joined Since {formatDate(organisation.created_at)}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button
                        startIcon={<ProjectIcon />}
                        onClick={() => setAssignModalOpen(true)}
                        sx={{
                            color: '#fff',
                            border: '1px solid rgba(255,255,255,0.12)',
                            bgcolor: 'rgba(255,255,255,0.03)',
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 700,
                            px: 2.2,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                        }}
                    >
                        Assign Project
                    </Button>
                    <Button
                        startIcon={<EditIcon />}
                        sx={{
                            color: '#fff',
                            border: '1px solid rgba(255,255,255,0.12)',
                            bgcolor: 'rgba(255,255,255,0.03)',
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 700,
                            px: 2.2,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                        }}
                    >
                        Edit Organisation
                    </Button>
                    <Button
                        startIcon={<PeopleIcon />}
                        onClick={() => navigate(`/admin/organisation/${organisation?.org_id}/members`)}
                        sx={{
                            color: '#001D12',
                            border: `1px solid ${alpha('#10B981', 0.4)}`,
                            bgcolor: '#10B981',
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 900,
                            px: 2.2,
                            '&:hover': { bgcolor: '#059669' },
                        }}
                    >
                        Manage Members
                    </Button>
                </Box>
            </Box>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <StatCard icon={PeopleIcon} label="Total Members" value={members.length} helper="Across organisation" color="#10B981" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <StatCard icon={ProjectIcon} label="Active Projects" value={assignedProjects.length} helper="Assigned to organisation" color="#3B82F6" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <StatCard icon={SiteIcon} label="Total Sites" value={totalSitesCount} helper="Across assigned projects" color="#10B981" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <StatCard icon={DeploymentIcon} label="Active Deployments" value="--" helper="Currently running" color="#6366F1" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <StatCard icon={FileIcon} label="Files Uploaded" value="--" helper="Across all projects" color="#F97316" />
                </Grid>
            </Grid>

            <Box
                sx={{
                    p: 3,
                    bgcolor: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.07)',
                    borderRadius: '12px',
                    mb: 3,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 3 }}>
                    <BusinessIcon sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 19 }} />
                    <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: '1.05rem' }}>
                        Organisation Information
                    </Typography>
                </Box>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                        <InfoItem icon={BusinessIcon} label="Organisation Name" value={organisation.name} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                        <InfoItem icon={DomainIcon} label="Organisation Domain" value={organisation.domain || '--'} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                        <InfoItem icon={ContactIcon} label="Primary Contact" value={primaryContact} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                        <InfoItem icon={CalendarIcon} label="Joined Since" value={formatDate(organisation.created_at)} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                        <InfoItem icon={StatusIcon} label="Status" value={formatStatusLabel(organisation.status)} color={statusColor} />
                    </Grid>
                </Grid>
            </Box>

            {/* Assigned Projects Section */}
            <Box
                sx={{
                    p: 3,
                    bgcolor: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.07)',
                    borderRadius: '12px',
                    mb: 3,
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                        <ProjectIcon sx={{ color: '#3B82F6', fontSize: 20 }} />
                        <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: '1.05rem' }}>
                            Assigned Projects
                        </Typography>
                        <Chip
                            label={assignedProjects.length}
                            size="small"
                            sx={{
                                height: 20,
                                bgcolor: alpha('#3B82F6', 0.15),
                                color: '#3B82F6',
                                fontWeight: 800,
                                fontSize: '0.7rem',
                            }}
                        />
                    </Box>
                    <Button
                        startIcon={<ProjectIcon />}
                        onClick={() => setAssignModalOpen(true)}
                        sx={{
                            color: '#10B981',
                            border: `1px solid ${alpha('#10B981', 0.35)}`,
                            bgcolor: 'rgba(16,185,129,0.06)',
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '0.8rem',
                            px: 1.8,
                            '&:hover': { bgcolor: 'rgba(16,185,129,0.12)' },
                        }}
                    >
                        Assign Project
                    </Button>
                </Box>

                {assignedProjects.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center', border: '1px dashed rgba(255,255,255,0.1)', borderRadius: '8px' }}>
                        <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.88rem', mb: 1.5 }}>
                            No projects assigned to this organisation yet.
                        </Typography>
                        <Button
                            variant="outlined"
                            onClick={() => setAssignModalOpen(true)}
                            sx={{
                                color: '#10B981',
                                borderColor: alpha('#10B981', 0.4),
                                textTransform: 'none',
                                fontWeight: 700,
                                fontSize: '0.78rem',
                            }}
                        >
                            + Assign a Project
                        </Button>
                    </Box>
                ) : (
                    <Grid container spacing={2}>
                        {assignedProjects.map((project) => (
                            <Grid key={project.project_id} size={{ xs: 12, sm: 6, md: 4 }}>
                                <Box
                                    sx={{
                                        p: 2.2,
                                        bgcolor: 'rgba(255,255,255,0.02)',
                                        border: '1px solid rgba(255,255,255,0.08)',
                                        borderRadius: '10px',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        transition: 'border-color 0.2s',
                                        '&:hover': { borderColor: alpha('#3B82F6', 0.4) },
                                    }}
                                >
                                    <Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                            <Typography
                                                onClick={() => navigate(`/admin/project/${project.project_id}`)}
                                                sx={{
                                                    color: '#fff',
                                                    fontWeight: 800,
                                                    fontSize: '0.95rem',
                                                    cursor: 'pointer',
                                                    '&:hover': { color: '#3B82F6' },
                                                }}
                                            >
                                                {project.project_name}
                                            </Typography>
                                            <Chip
                                                label={project.project_status || 'UNKNOWN'}
                                                size="small"
                                                sx={{
                                                    height: 18,
                                                    fontSize: '0.58rem',
                                                    fontWeight: 800,
                                                    bgcolor: alpha('#10B981', 0.12),
                                                    color: '#10B981',
                                                }}
                                            />
                                        </Box>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6, my: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                                <SiteIcon sx={{ fontSize: 15, color: 'rgba(255,255,255,0.45)' }} />
                                                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.78rem' }}>
                                                    Location: <Box component="span" sx={{ color: '#fff', fontWeight: 600 }}>{project.country || 'N/A'}</Box>
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                                <ProjectIcon sx={{ fontSize: 15, color: 'rgba(255,255,255,0.45)' }} />
                                                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.78rem' }}>
                                                    Sites: <Box component="span" sx={{ color: '#10B981', fontWeight: 700 }}>{project.sites_count ?? (project as any).sites?.length ?? 0}</Box>
                                                </Typography>
                                            </Box>
                                            {project.assignment?.user && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                                    <PeopleIcon sx={{ fontSize: 15, color: 'rgba(255,255,255,0.45)' }} />
                                                    <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.78rem' }}>
                                                        Fieldworker: <Box component="span" sx={{ color: '#fff', fontWeight: 600 }}>{project.assignment.user.first_name} {project.assignment.user.last_name}</Box>
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2, pt: 1.5, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                                        <Button
                                            size="small"
                                            onClick={() => handleUnassignProject(project.project_id)}
                                            sx={{
                                                color: '#EF4444',
                                                textTransform: 'none',
                                                fontWeight: 700,
                                                fontSize: '0.7rem',
                                                '&:hover': { bgcolor: 'rgba(239,68,68,0.1)' },
                                            }}
                                        >
                                            Unassign
                                        </Button>
                                    </Box>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>

            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1 }}>
                    <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: '1.25rem' }}>
                        Organisation Tools & Entitlements
                    </Typography>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#10B981' }} />
                </Box>
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.82rem', mb: 2.5 }}>
                    Manage tool entitlements for this organisation. Only active organisation tools can be enabled for projects.
                </Typography>
                <Grid container spacing={2}>
                    {(() => {
                        const fallbackCatalog: SystemTool[] = [
                            { tool_id: 'bioacoustic', slug: 'bioacoustic', name: 'Bioacoustics', tool_name: 'bioacoustic', display_name: 'Bioacoustics', description: 'Detect and classify wildlife sounds and bird species.', is_active: true },
                            { tool_id: 'flora_intelligence', slug: 'flora_intelligence', name: 'Flora Intelligence', tool_name: 'flora_intelligence', display_name: 'Flora Intelligence', description: 'Identify plant species and assess vegetation health.', is_active: true },
                            { tool_id: 'species_vision', slug: 'species_vision', name: 'Species Vision', tool_name: 'species_vision', display_name: 'Species Vision', description: 'AI models to identify species using computer vision.', is_active: true },
                            { tool_id: 'nature_metrics', slug: 'nature_metrics', name: 'Nature Metrics', tool_name: 'nature_metrics', display_name: 'Nature Metrics', description: 'Quantify biodiversity, habitat health and ecosystem integrity.', is_active: true },
                            { tool_id: 'tnfd_analytics', slug: 'tnfd_analytics', name: 'TNFD Analytics', tool_name: 'tnfd_analytics', display_name: 'TNFD Analytics', description: 'Generate TNFD aligned reports and disclosures.', is_active: true },
                            { tool_id: 'climate_intelligence', slug: 'climate_intelligence', name: 'Climate Intelligence', tool_name: 'climate_intelligence', display_name: 'Climate Intelligence', description: 'Assess climate risks, vulnerability and adaptation strategies.', is_active: true },
                        ];
                        const catalog = systemTools.length > 0 ? systemTools : fallbackCatalog;
                        return catalog.map((tool) => {
                            const tSlug = (tool.slug || tool.tool_name || tool.tool_id || '').toLowerCase();
                            const tName = (tool.name || tool.display_name || '').toLowerCase();
                            const tId = String(tool.tool_id || '').toLowerCase();

                            const orgTool = orgTools.find((ot) => {
                                if (!ot) return false;
                                const otId = String(ot.tool_id || '').toLowerCase();
                                const otOrgToolId = String(ot.org_tool_id || '').toLowerCase();
                                if (otId && tId && otId === tId) return true;
                                if (otOrgToolId && tId && otOrgToolId === tId) return true;

                                if (ot.tool) {
                                    const nestedId = String(ot.tool.tool_id || '').toLowerCase();
                                    const nestedSlug = (ot.tool.slug || ot.tool.tool_name || ot.tool.tool_id || '').toLowerCase();
                                    const nestedName = (ot.tool.name || ot.tool.display_name || '').toLowerCase();
                                    if (nestedId && tId && nestedId === tId) return true;
                                    if (nestedSlug && tSlug && (nestedSlug === tSlug || nestedSlug.replace(/_/g, '-') === tSlug.replace(/_/g, '-'))) return true;
                                    if (nestedName && tName && nestedName === tName) return true;
                                }

                                if (otId && tSlug && (otId === tSlug || otId.replace(/_/g, '-') === tSlug.replace(/_/g, '-'))) return true;
                                return false;
                            });

                            const isProvisioned = !!orgTool;
                            const isActive = orgTool?.status === 'active';
                            const isSuspended = orgTool?.status === 'suspended';

                            let statusLabel = 'NOT PROVISIONED';
                            let statusColor = '#9CA3AF';
                            let cardBorderColor = alpha('#fff', 0.07);
                            let cardBgColor = 'rgba(255,255,255,0.02)';

                            if (isActive) {
                                statusLabel = 'ACTIVE';
                                statusColor = '#10B981';
                                cardBorderColor = alpha('#10B981', 0.35);
                                cardBgColor = 'rgba(16,185,129,0.06)';
                            } else if (isSuspended) {
                                statusLabel = 'SUSPENDED';
                                statusColor = '#F59E0B';
                                cardBorderColor = alpha('#F59E0B', 0.3);
                                cardBgColor = 'rgba(245,158,11,0.04)';
                            }

                            const isLoadingThis = actionLoadingId === (orgTool?.org_tool_id || tool.tool_id);

                            return (
                                <Grid key={tool.tool_id} size={{ xs: 12, sm: 6, lg: 4 }}>
                                    <Box
                                        sx={{
                                            minHeight: 200,
                                            p: 2.5,
                                            borderRadius: '12px',
                                            border: `1px solid ${cardBorderColor}`,
                                            bgcolor: cardBgColor,
                                            position: 'relative',
                                            overflow: 'hidden',
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}
                                    >
                                        <Box sx={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                                <Chip
                                                    label={statusLabel}
                                                    size="small"
                                                    sx={{
                                                        height: 20,
                                                        bgcolor: alpha(statusColor, 0.12),
                                                        color: statusColor,
                                                        border: `1px solid ${alpha(statusColor, 0.3)}`,
                                                        fontSize: '0.58rem',
                                                        fontWeight: 900,
                                                    }}
                                                />
                                                {isActive && (
                                                    <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.62rem' }}>
                                                        Enabled for Org
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: '1.15rem', lineHeight: 1.1, mb: 1 }}>
                                                {tName}
                                            </Typography>
                                            <Typography sx={{ color: 'rgba(255,255,255,0.56)', fontSize: '0.74rem', lineHeight: 1.45, mb: 2 }}>
                                                {tool.description || 'Environmental intelligence & monitoring module.'}
                                            </Typography>

                                            <Box sx={{ mt: 'auto', pt: 1 }}>
                                                {!isProvisioned && (
                                                    <Button
                                                        fullWidth
                                                        variant="contained"
                                                        disabled={isLoadingThis}
                                                        onClick={() => handleProvisionTool(tool.tool_id)}
                                                        sx={{
                                                            bgcolor: '#10B981',
                                                            color: '#001D12',
                                                            borderRadius: '7px',
                                                            textTransform: 'none',
                                                            fontWeight: 900,
                                                            fontSize: '0.72rem',
                                                            '&:hover': { bgcolor: '#059669' },
                                                        }}
                                                    >
                                                        {isLoadingThis ? <CircularProgress size={16} sx={{ color: '#001D12' }} /> : 'Grant / Provision Tool'}
                                                    </Button>
                                                )}

                                                {isActive && (
                                                    <Button
                                                        fullWidth
                                                        variant="outlined"
                                                        disabled={isLoadingThis}
                                                        onClick={() => handleSuspendTool(orgTool!.org_tool_id)}
                                                        sx={{
                                                            color: '#EF4444',
                                                            borderColor: alpha('#EF4444', 0.4),
                                                            borderRadius: '7px',
                                                            textTransform: 'none',
                                                            fontWeight: 800,
                                                            fontSize: '0.72rem',
                                                            '&:hover': { borderColor: '#EF4444', bgcolor: 'rgba(239,68,68,0.08)' },
                                                        }}
                                                    >
                                                        {isLoadingThis ? <CircularProgress size={16} sx={{ color: '#EF4444' }} /> : 'Suspend Tool'}
                                                    </Button>
                                                )}

                                                {isSuspended && (
                                                    <Button
                                                        fullWidth
                                                        variant="contained"
                                                        disabled={isLoadingThis}
                                                        onClick={() => handleActivateTool(orgTool!.org_tool_id)}
                                                        sx={{
                                                            bgcolor: '#10B981',
                                                            color: '#001D12',
                                                            borderRadius: '7px',
                                                            textTransform: 'none',
                                                            fontWeight: 900,
                                                            fontSize: '0.72rem',
                                                            '&:hover': { bgcolor: '#059669' },
                                                        }}
                                                    >
                                                        {isLoadingThis ? <CircularProgress size={16} sx={{ color: '#001D12' }} /> : 'Re-activate Tool'}
                                                    </Button>
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>
                                </Grid>
                            );
                        });
                    })()}
                </Grid>
            </Box>



            {/* Organisation Assignment Modal */}
            <AssignProjectOrgModal
                open={assignModalOpen}
                onClose={() => setAssignModalOpen(false)}
                defaultOrgId={organisation?.org_id}
                onAssigned={fetchOverview}
            />
        </Box>
    );
};

export default OrganisationOverviewPage;

